import type { RumGlobal, RumInitConfiguration } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect } from 'react';

import { getDeploymentEnvironment, getOrigins, TBD_VERSION } from '../index.js';
import type { DeploymentEnvironment } from '../index.js';

export type { RumInitConfiguration } from '@datadog/browser-rum';

export type RumEnv = 'production' | 'uat' | 'development' | 'feature' | 'local';
export function deploymentEnvToRumEnv(
  deploymentEnv?: DeploymentEnvironment
): RumEnv {
  if (deploymentEnv === 'production') return 'production';
  if (deploymentEnv === 'staging') return 'uat';
  if (deploymentEnv === 'dev') return 'development';
  if (deploymentEnv === 'local') return 'local';
  return 'feature';
}

export function getBaseRumConfig({ hostname }: { hostname: string }) {
  const deploymentEnvironment = getDeploymentEnvironment({ hostname });
  const rumEnv = deploymentEnvToRumEnv(deploymentEnvironment);
  const allowedOrigins = Object.values(getOrigins({ hostname }));

  const config: Omit<
    RumInitConfiguration,
    'site' | 'service' | 'clientToken' | 'applicationId'
  > = {
    env: rumEnv,
    version: TBD_VERSION,
    enableExperimentalFeatures: ['clickmap'],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'allow',
    silentMultipleInit: true,
    allowedTracingUrls: [
      function (givenUrl) {
        const givenOrigin = new URL(givenUrl).origin;
        const isAllowedOrigin = allowedOrigins.includes(givenOrigin);

        return isAllowedOrigin;
      },
    ],
    beforeSend: function beforeSend(event) {
      // console.log('beforeSend', event);
      // Current state (all Chromiums; no FF nor Safari): https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection
      // Very old/early API: https://www.davidbcalhoun.com/2010/optimizing-based-on-connection-speed-using-navigator.connection-on-android-2.2-/
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

      if (connection) {
        // tried the 'event.context = {...event.context, connection}' approach
        // shown in https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#enrich-rum-events
        // and some others (Object.assign, etc) but for some reason 'connection' cannot be directly applied to context
        // so pull the individual properties off
        const { type, effectiveType, downlink, downlinkMax, rtt } = connection;

        // 'event.context.connection = { type, etc} ' worked locally, but sticking with the syntax shown in
        //  https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#enrich-rum-events
        event.context = {
          ...event.context,
          connection: {
            type,
            effectiveType,
            downlink,
            downlinkMax,
            rtt,
          },
        };
      }
      return true;
    },
  };

  return config;
}

type User = Parameters<RumGlobal['setUser']>[0] & {
  id: string;
};
export function useRumEffect<RD = unknown>(
  rootData: RD | undefined,
  getRumConfig: (rd: RD) => RumInitConfiguration,
  getRumUser?: (rd: RD) => User | undefined
) {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (rootData) {
      if (getRumUser) {
        const rumUser = getRumUser(rootData);
        if (rumUser) {
          datadogRum.setUser(rumUser);
        }
      }
      const config = getRumConfig(rootData);
      console.log('init datadog rum', config);
      datadogRum.init(config);
    }
  }, [getRumConfig, getRumUser, rootData]);
}
